import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useApp, GIGS } from '@/context/AppContext';
import { Check, Loader2, ArrowRight, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, setActiveOrder } = useApp();
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [order, setOrder] = useState(null);

  const orderId = searchParams.get('order_id');
  const gateway = searchParams.get('gateway');

  useEffect(() => {
    if (!orderId) {
      setStatus('error');
      return;
    }

    const checkOrder = async () => {
      try {
        // For PayMongo redirect flow, the webhook may not have fired yet.
        // Poll a few times to check if payment was processed.
        let attempts = 0;
        const maxAttempts = 10;
        const interval = setInterval(async () => {
          attempts++;
          try {
            const resp = await axios.get(`${API}/orders/${orderId}`);
            const orderData = resp.data;
            if (orderData.status === 'IN_DEVELOPMENT') {
              clearInterval(interval);
              setOrder(orderData);
              setStatus('success');

              // Set up app context
              const gig = GIGS.find(g => g.id === orderData.gig_id);
              if (gig) {
                setUser({ name: 'Client', email: 'client@medialabs.com' });
                setActiveOrder({
                  id: orderData.order_id,
                  gig,
                  tier: gig.tiers[orderData.tier_index || 0],
                  tierIndex: orderData.tier_index || 0,
                  total: orderData.total,
                  deposit: orderData.deposit,
                  status: orderData.status,
                  phases: orderData.phases,
                  deliverables: orderData.deliverables,
                });
              }
            } else if (attempts >= maxAttempts) {
              clearInterval(interval);
              // Payment may still be processing via webhook
              setOrder(orderData);
              setStatus('success');
            }
          } catch (err) {
            if (attempts >= maxAttempts) {
              clearInterval(interval);
              setStatus('error');
            }
          }
        }, 2000);

        return () => clearInterval(interval);
      } catch (err) {
        setStatus('error');
      }
    };

    checkOrder();
    // API and GIGS are module-level constants that never change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, setUser, setActiveOrder]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center" data-testid="payment-success-page">
      <div className="max-w-md w-full px-4">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-[color:var(--hairline)] shadow-[var(--shadow-soft)] p-8 text-center"
        >
          {status === 'loading' && (
            <>
              <Loader2 className="w-12 h-12 text-brand-blue animate-spin mx-auto mb-4" />
              <h2 className="text-lg font-bold text-foreground mb-2">Confirming Payment...</h2>
              <p className="text-sm text-muted-foreground">Please wait while we verify your payment with {gateway || 'the payment provider'}.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-500" strokeWidth={2.5} />
              </div>
              <h2 className="text-xl font-bold text-foreground mb-2">Sprint Launched!</h2>
              <p className="text-sm text-muted-foreground mb-1">
                Your deposit for <strong>{order?.gig_title}</strong> has been confirmed.
              </p>
              <p className="text-xs text-muted-foreground mb-6">
                Order: {order?.order_id} &middot; via {order?.payment_gateway || gateway}
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full py-3 rounded-xl bg-brand-blue text-white text-sm font-bold shadow-[var(--shadow-soft)] hover:bg-brand-blue-dark transition-colors duration-200 inline-flex items-center justify-center gap-2"
                data-testid="goto-dashboard-btn"
              >
                Go to Your Dashboard <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-lg font-bold text-foreground mb-2">Something went wrong</h2>
              <p className="text-sm text-muted-foreground mb-6">We couldn't verify your payment. Please contact support.</p>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 text-sm font-semibold text-brand-blue hover:text-brand-blue-dark"
              >
                Back to Services <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
